"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useCart } from "./cart-context";
import { CustomizationSwatches } from "@/components/commerce/customization-swatches";

export function CartMenu() {
  const { cart, count, isOpen, toggleCart, closeCart, removeItem } = useCart();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) closeCart();
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen, closeCart]);

  return (
    <div className="cart-menu" ref={menuRef}>
      <button
        type="button"
        className="cart-icon-btn"
        aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`}
        aria-expanded={isOpen}
        onClick={toggleCart}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M3 4h2l2.4 12.2a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L21 8H6"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="9.5" cy="21" r="1.4" fill="currentColor" />
          <circle cx="17.5" cy="21" r="1.4" fill="currentColor" />
        </svg>
        {count > 0 && <span className="cart-badge">{count}</span>}
      </button>

      {isOpen && (
        <div className="cart-dropdown">
          <div className="cart-dropdown-header">
            <span className="cart-dropdown-title">Your Cart</span>
            <button type="button" className="cart-dropdown-close" aria-label="Close" onClick={closeCart}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1.5 1.5l11 11M12.5 1.5l-11 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {!cart || cart.items.length === 0 ? (
            <p className="cart-empty">Your cart is empty.</p>
          ) : (
            <>
              <ul className="cart-dropdown-list">
                {cart.items.map((item) => (
                  <li key={item.selectionId} className="cart-dropdown-item">
                    <div className="cart-dropdown-item-info">
                      <span className="cart-dropdown-item-name">
                        {item.productName}
                        {item.customizationSwatches.length > 0 && (
                          <>
                            {" "}
                            <CustomizationSwatches swatches={item.customizationSwatches} />
                          </>
                        )}
                      </span>
                      <span className="mono cart-dropdown-item-meta">
                        Qty {item.quantity} · ${item.unitPrice.toFixed(2)} each
                      </span>
                    </div>
                    {/* Trash icon, deliberately not an "x" -- that glyph is now the dropdown's own close
                        button above, and a second, different-meaning "x" next to each line was exactly
                        what made this read as "close" instead of "remove this item". */}
                    <button
                      type="button"
                      className="cart-dropdown-remove"
                      aria-label={`Remove ${item.productName}`}
                      onClick={() => removeItem(item.selectionId)}
                    >
                      {/* Solid silhouette, no internal ribs -- a thin-stroke outline with grooves down
                          the body was the thing that blended into a smudge at this size. */}
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <rect x="5.5" y="1.2" width="5" height="1.8" rx="0.6" />
                        <rect x="2" y="3" width="12" height="1.6" rx="0.8" />
                        <path d="M3 5.1H13L12.2 13.2A1.3 1.3 0 0 1 11 14.5H5A1.3 1.3 0 0 1 3.8 13.2Z" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="cart-dropdown-footer">
                <span className="mono">Subtotal ${cart.subtotal.toFixed(2)}</span>
                <Link href="/cart" className="btn-secondary" onClick={closeCart}>
                  View Cart
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
